// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson
// Additional terms under AGPL section 7(b) apply — see ADDITIONAL-TERMS.md.

/**
 * The target list. RESEARCH §1: "three providers" is a fiction. The tool SHAPE and the accepted
 * keyword subset vary independently, and one belt can be legal on one API of a provider and a 400 on
 * the other.
 *
 * Two profiles named in RESEARCH §1's target table are deliberately NOT `TargetId`s:
 *   - `openai-strict-finetuned` — a delta on `openai-strict` (§2), not a separate column.
 *   - `azure-openai` — RESEARCH §2 is emphatic that Azure is a WARNING PROFILE ONLY, never a
 *     transform. It lives in `limits.ts` as `AZURE_DIVERGENCE`, so no transpiler can accidentally
 *     acquire an "Azure-conservative" mode that strips working constraints.
 */

import type { TargetDef, TargetId } from '../types.ts';

export const TARGETS: readonly TargetDef[] = [
  {
    id: 'openai-strict',
    label: 'OpenAI — strict:true',
    short: 'OpenAI strict',
    family: 'openai',
    blurb:
      'Base models, both APIs. The keyword subset is identical across Responses and Chat Completions; only the tool shape and the error `param` path differ.',
    emits: 'json',
  },
  {
    id: 'openai-nonstrict-responses',
    label: 'OpenAI Responses — strict omitted or false',
    short: 'OpenAI Responses (non-strict)',
    family: 'openai',
    blurb:
      'The keyword subset is still validated at request time. "Just turn strict off" is not an escape hatch — Responses rejects `oneOf` even when `strict` is omitted.',
    emits: 'json',
  },
  {
    id: 'openai-nonstrict-chat',
    label: 'OpenAI Chat Completions — non-strict (the default)',
    short: 'OpenAI Chat (non-strict)',
    family: 'openai',
    blurb:
      'Advisory only: the schema is a prompt hint and nothing is enforced. Anything here that another column rejects still breaks the moment you enable strict.',
    emits: 'json',
  },
  {
    id: 'anthropic-strict',
    label: 'Anthropic — strict:true',
    short: 'Anthropic strict',
    family: 'anthropic',
    blurb:
      'Grammar-constrained. Keeps optional properties, `default` and `allOf`; loses every numeric and string bound. Three request-wide budgets apply: 20 strict tools, 24 optional params, 16 union-typed params.',
    emits: 'json',
  },
  {
    id: 'anthropic-nonstrict',
    label: 'Anthropic — input_schema, no strict',
    short: 'Anthropic (non-strict)',
    family: 'anthropic',
    blurb:
      'Rejects `oneOf`/`allOf`/`anyOf` at the top level of `input_schema` and otherwise treats the schema as a hint. No published cap on tool count or total schema size.',
    emits: 'json',
  },
  {
    id: 'anthropic-sdk',
    label: 'Anthropic — via the official SDK',
    short: 'Anthropic SDK',
    family: 'anthropic',
    blurb:
      'The Python/TS/Ruby/PHP SDKs rewrite your schema before sending it, then validate the response against your original. Five constraints survive only as prose in the description.',
    emits: 'json',
  },
  {
    id: 'gemini-parameters',
    label: 'Gemini — functionDeclarations[].parameters',
    short: 'Gemini parameters',
    family: 'gemini',
    blurb:
      'The 22-field OpenAPI subset, identical in v1beta and v1 GA. The lossiest target: no `$ref`, no `$defs`, no `additionalProperties`, and `type` is a scalar proto enum.',
    emits: 'json',
  },
  {
    id: 'gemini-jsonschema',
    label: 'Gemini — functionDeclarations[].parametersJsonSchema',
    short: 'Gemini JSON Schema',
    family: 'gemini',
    blurb:
      'v1beta only — absent from Gemini API v1 (GA) — and mutually exclusive with `parameters`. Google publishes no supported-keyword list for this field, so most of this column is honestly unknown.',
    emits: 'json',
  },
  {
    id: 'vertex-parameters',
    label: 'Vertex AI — parameters',
    short: 'Vertex parameters',
    family: 'gemini',
    blurb:
      'Strictly larger than the Gemini API subset: 25 fields, adding `ref`, `defs` and `additionalProperties`. Tool names must additionally start with a letter or an underscore.',
    emits: 'json',
  },
  {
    id: 'gbnf-llamacpp',
    label: 'GBNF — llama.cpp',
    short: 'GBNF (llama.cpp)',
    family: 'gbnf',
    blurb:
      'What `llama-server` actually runs. Half the drops are conditional on sibling keywords, so the drop set is computed per schema node, not fixed.',
    emits: 'gbnf',
  },
  {
    id: 'gbnf-xgrammar',
    label: 'GBNF — xgrammar / vLLM',
    short: 'GBNF (xgrammar)',
    family: 'gbnf',
    blurb:
      "Same keyword handling as llama.cpp, two emission changes: xgrammar rejects the `space` rule's bare leading `|` and any rule name starting with a digit.",
    emits: 'gbnf',
  },
];

const BY_ID: ReadonlyMap<TargetId, TargetDef> = new Map(TARGETS.map((t) => [t.id, t]));

export function targetById(id: TargetId): TargetDef | undefined {
  return BY_ID.get(id);
}

export const TARGET_IDS: readonly TargetId[] = TARGETS.map((t) => t.id);
